import { DateTime } from 'luxon';
import { z } from 'zod';
import { UnixTimestamp } from 'libs/end-to-end/src/types/databases/analysis/database-analysis';
import { differenceInMonths } from 'date-fns';
import { isValid } from 'date-fns';
import { parseISO } from 'date-fns';
import { isSameMonth } from 'date-fns';
import { differenceInYears } from 'date-fns';
import { DateRange } from 'libs/end-to-end/src/date-range';

export const DateComponents = z.object({
  year: z.number(),
  month: z.number(),
  day: z.number(),
});

export type DateComponents = z.infer<typeof DateComponents>;

export const getTimestampAsReadableString = (timestamp: number) => {
  return DateTime.fromMillis(timestamp)
    .toLocaleString(DateTime.DATETIME_FULL)
    .replace(/\u202F/g, ' ');
};

export const getCurrentDateAsReadableString = () => {
  return DateTime.now()
    .toLocaleString(DateTime.DATE_FULL)
    .replace(/\u202F/g, ' ');
};

export const getDateStringAsReadableString = (dateString: string) => {
  return DateTime.fromISO(dateString)
    .toLocaleString(DateTime.DATETIME_FULL)
    .replace(/\u202F/g, ' ');
};

export const convertDateTimeFromISOToYYYYMMDD = (dateTime: string) => {
  return DateTime.fromISO(dateTime).toFormat('yyyy-MM-dd');
};

export const getDateTimeAsReadableString = (date: Date) => {
  return DateTime.fromJSDate(date)
    .toLocaleString(DateTime.DATETIME_FULL)
    .replace(/\u202F/g, ' ');
};

export const getUnixTimestampAsReadableString = (date: UnixTimestamp) => {
  return DateTime.fromJSDate(new Date(date))
    .toLocaleString(DateTime.DATETIME_FULL)
    .replace(/\u202F/g, ' ');
};

export const getDateAsReadableString = (date: Date) => {
  return DateTime.fromJSDate(date).toLocaleString(DateTime.DATE_FULL);
};

export const isValidYYYYMMDD = (date: string) => {
  const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
  return dateRegex.test(date);
};

export const getDateAsYYYYMMDD = (date: Date) => {
  return DateTime.fromJSDate(date).toFormat('yyyy-MM-dd');
};

export const getYYYYMMDDAsDate = (date: string): Date => {
  return DateTime.fromFormat(date, 'yyyy-MM-dd').toJSDate();
};

export const getDateAsMMMDDYYYY = (date: Date) => {
  return DateTime.fromJSDate(date).toFormat('MMM dd, yyyy');
};

export type Week = (number | undefined)[];

export const generateMonthCalendar = (year: number, month: number): Week[] => {
  const calendar: Week[] = [];
  const firstDay = new Date(year, month, 1);
  const startingDayOffset = (firstDay.getDay() + 6) % 7;

  let currentWeek: Week = [];

  for (let i = 0; i < startingDayOffset; i++) {
    currentWeek.push(undefined);
  }

  for (let day = 1; day <= getDaysInMonth(year, month); day++) {
    currentWeek.push(day);

    if (currentWeek.length === 7) {
      calendar.push(currentWeek);
      currentWeek = [];
    }
  }

  if (currentWeek.length > 0) {
    while (currentWeek.length < 7) {
      currentWeek.push(undefined);
    }
    calendar.push(currentWeek);
  }

  return calendar;
};

export const getDaysInMonth = (year: number, month: number) => {
  return new Date(year, month + 1, 0).getDate();
};

export const areDaysEqual = (date1: Date, date2: Date) => {
  return (
    date1.getDate() === date2.getDate() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getFullYear() === date2.getFullYear()
  );
};

export const isDateInRange = (
  date: Date,
  startDate: Date,
  endDate: Date | null,
) => {
  return (
    date.getTime() >= startDate.getTime() &&
    (endDate ? date.getTime() <= endDate.getTime() : false)
  );
};

export const isDateStartOrEnd = (
  date: Date,
  startDate: Date,
  endDate: Date | null,
) => {
  return (
    areDaysEqual(date, startDate) ||
    (endDate ? areDaysEqual(date, endDate) : false)
  );
};

export const DateDuration = z.object({
  years: z.number(),
  months: z.number(),
  totalMonths: z.number(),
});

export type DateDuration = z.infer<typeof DateDuration>;

export const getDateDuration = (range: DateRange): DateDuration => {
  const { start, end } = range;
  const [a, b] = start <= end ? [start, end] : [end, start];

  let years = differenceInYears(b, a);
  let totalMonths = differenceInMonths(b, a);
  let months = totalMonths - years * 12;

  if (isSameMonth(a, b)) {
    totalMonths = 1;
    months = 1;
    years = 0;
  } else {
    totalMonths += 1;

    years = Math.floor(totalMonths / 12);
    months = totalMonths % 12;
  }

  return { years, months, totalMonths };
};

type FormatDateStandardProps =
  | {
      date?: Date;
      formatter: Intl.DateTimeFormat;
      style?: never;
    }
  | {
      date?: Date;
      formatter?: never;
      style?: Intl.DateTimeFormatOptions['dateStyle'] | 'iso';
    };

/**
 * Formats a date into the user's standard localized short format,
 * such as `dd/MM/yyyy` or `MM/dd/yyyy`, depending on the locale.
 *
 * The `type` parameter controls the level of detail:
 * - `'short'`: "02/11/2025"
 * - `'medium'`: "Nov 2, 2025"
 * - `'long'`: "November 2, 2025"
 * - `'full'`: "Sunday, November 2, 2025"
 * - `'utc'`: "2025-11-02T00:00:00.000Z"
 *
 * This uses the built-in `Intl.DateTimeFormat` API to automatically
 * detect or apply the appropriate regional format.
 *
 * @example
 * ```ts
 * formatDateStandard({ date: new Date('2025-11-02') });
 * // "02/11/2025" in en-GB
 *
 * formatDateStandard({ date: new Date('2025-11-02'), locales: 'en-US' });
 * // "11/02/2025" in en-US
 * ```
 *
 * @param {object} props - The formatting options.
 * @param {Date} [props.date] - The date to format. Defaults to the current date/time.
 * @param {Intl.LocalesArgument} [props.locales] - The locale(s) to use for formatting.
 *   If omitted, the browser or runtime default locale is used.
 *
 * @returns {string} The formatted date string, localized according to the given (or default) locale.
 */
export const formatDateStandard = ({
  date,
  formatter,
  style,
}: FormatDateStandardProps): string => {
  const d = date ?? new Date();

  if (formatter) {
    return formatter.format(d);
  }

  if (style === 'iso') {
    return d.toISOString();
  }

  return new Intl.DateTimeFormat(undefined, {
    dateStyle: style,
  }).format(d);
};

/**
 * Returns the standard date pattern string (e.g. "dd/MM/yyyy" or "MM/dd/yyyy")
 * for the given or current locale, inferred from Intl.DateTimeFormat.
 *
 * @param {Intl.LocalesArgument} [locales] - The locale(s) to use. Defaults to the user's current locale.
 * @returns {string} The localized short date format pattern.
 *
 * @example
 * getDateFormatPattern(formatter);         // "MM/dd/yyyy" (US)
 * getDateFormatPattern(formatter);  // "dd/MM/yyyy" (GB)
 * getDateFormatPattern(formatter);  // "dd.MM.yyyy" (DE)
 */
export const getStandardDateFormatPattern = (
  formatter: Intl.DateTimeFormat,
): string => {
  const parts = formatter.formatToParts(new Date());
  return parts
    .map((p) => {
      switch (p.type) {
        case 'day':
          return 'dd';
        case 'month':
          return 'MM';
        case 'year':
          return 'yyyy';
        default:
          return p.value; // separator or literal (e.g. '/', '.', '-')
      }
    })
    .join('');
};

export const tryParseISO = (dateString: string): Date | null => {
  try {
    const d = parseISO(dateString);
    return isValid(d) ? d : null;
  } catch {
    return null;
  }
};
